import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, Heart, ShoppingCart, ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import { useCart } from '../../Context/CartContext';
import categoryService from '../../services/categoryService';
import SearchModal from '../SearchModal/SearchModal';

const Navbar = () => {
  const [categories, setCategories] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isPagesHovered, setIsPagesHovered] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isPagesExpanded, setIsPagesExpanded] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(73);
  const headerRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const { user, logout } = useAuth();
  const { openCart, getCartCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const response = await categoryService.getCategories();
        if (response.success) {
          setCategories(response.categories);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, [categories]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isDrawerOpen]);

  const handleSearch = () => {
    setIsSearchOpen(true);
  };

  const handleUserClick = () => {
    if (user) {
      // Could navigate to user profile or show dropdown
      // For now, show logout option
      if (window.confirm('Do you want to logout?')) {
        logout();
        navigate('/');
      }
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      <header ref={headerRef} className="bg-white shadow-sm sticky top-0 z-50 relative">
        <div className="relative w-full">
          <div className="max-w-7xl mx-auto px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-12">
                <Link to="/" className="flex flex-col items-center text-[#6B9FA8]">
                  <div className="text-3xl font-bold leading-none">
                    <svg width="70" height="60" viewBox="0 0 70 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 10 L25 45 L15 45 L5 15 Z" fill="#6B9FA8" opacity="0.6" />
                      <path d="M20 10 L35 45 L25 45 L15 15 Z" fill="#6B9FA8" opacity="0.8" />
                      <path d="M30 10 L45 45 L35 45 L25 15 Z" fill="#6B9FA8" />
                    </svg>
                  </div>
                  <div className="text-[10px] tracking-wider">EST. 2020</div>
                </Link>

                <nav className="hidden md:flex items-center space-x-8 text-sm">
                  {isLoadingCategories ? (
                    // Skeleton loading for all nav links
                    <>
                      <div className="h-4 w-12 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                    </>
                  ) : (
                    // Render all nav links at once when categories are loaded
                    <>
                      <Link to="/" className="hover:text-[#6B9FA8] transition-colors">Home</Link>
                      {categories.map((category) => (
                        <Link
                          key={category._id || category.name}
                          to={`/category/${category._id}`}
                          className="hover:text-[#6B9FA8] transition-colors"
                        >
                          {category.name || category.category}
                        </Link>
                      ))}
                      <div
                        className="relative"
                        onMouseEnter={() => {
                          if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                            closeTimeoutRef.current = null;
                          }
                          setIsPagesHovered(true);
                        }}
                        onMouseLeave={() => {
                          closeTimeoutRef.current = setTimeout(() => {
                            setIsPagesHovered(false);
                          }, 300);
                        }}
                      >
                        <button className="flex items-center hover:text-[#6B9FA8] transition-colors">
                          Pages <ChevronDown className="ml-1 w-4 h-4" />
                        </button>
                        {/* Extended hover area to bridge gap */}
                        <div className="absolute top-full left-0 right-0 h-4 bg-transparent pointer-events-auto"></div>
                      </div>
                    </>
                  )}
                </nav>
              </div>

              <div className="flex items-center space-x-5">
                <button
                  onClick={handleSearch}
                  className="hover:text-[#6B9FA8] transition-colors"
                  title="Search"
                >
                  <Search className="w-5 h-5" />
                </button>
                <button
                  onClick={handleUserClick}
                  className="hover:text-[#6B9FA8] transition-colors"
                  title={user ? `Logged in as ${user.Firstname || user.email}` : 'Login'}
                >
                  <User className="w-5 h-5" />
                </button>
                <button className="relative hover:text-[#6B9FA8] transition-colors">
                  <Heart className="w-5 h-5" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">0</span>
                </button>
                <button
                  onClick={openCart}
                  className="relative hover:text-[#6B9FA8] transition-colors"
                  title="Shopping cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {getCartCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {getCartCount()}
                    </span>
                  )}
                </button>

                {/* Mobile Burger Menu Button */}
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className="md:hidden hover:text-[#6B9FA8] transition-colors"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Full Width Mega Menu - Behind Navbar */}
        <div
          className={`fixed left-0 right-0 bg-white shadow-lg border-t border-gray-200 z-30 transition-all duration-300 ease-in-out ${isPagesHovered
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none'
            }`}
          style={{ top: `${headerHeight}px`, width: '100%' }}
          onMouseEnter={() => {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
              closeTimeoutRef.current = null;
            }
            setIsPagesHovered(true);
          }}
          onMouseLeave={() => {
            closeTimeoutRef.current = setTimeout(() => {
              setIsPagesHovered(false);
            }, 300);
          }}
        >
          {/* Transparent bridge extending upward to cover gap */}
          <div
            className="absolute left-0 right-0 bg-transparent pointer-events-auto"
            style={{ bottom: '100%', height: '20px' }}
          ></div>
          <div className="w-full">
            <div className="max-w-7xl mx-auto px-8 py-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Link
                  to="/aboutus"
                  className="group block p-6 border border-gray-200 rounded-lg hover:border-[#6B9FA8] hover:shadow-md transition-all"
                  onClick={() => {
                    setIsPagesHovered(false);
                    if (closeTimeoutRef.current) {
                      clearTimeout(closeTimeoutRef.current);
                      closeTimeoutRef.current = null;
                    }
                  }}
                >
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 group-hover:text-[#6B9FA8] transition-colors">
                    About us
                  </h3>
                  <p className="text-sm text-gray-600">
                    Learn more about our company, mission, and values
                  </p>
                </Link>
                <Link
                  to="/contactus"
                  className="group block p-6 border border-gray-200 rounded-lg hover:border-[#6B9FA8] hover:shadow-md transition-all"
                  onClick={() => {
                    setIsPagesHovered(false);
                    if (closeTimeoutRef.current) {
                      clearTimeout(closeTimeoutRef.current);
                      closeTimeoutRef.current = null;
                    }
                  }}
                >
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 group-hover:text-[#6B9FA8] transition-colors">
                    Contact us
                  </h3>
                  <p className="text-sm text-gray-600">
                    Get in touch with our team for inquiries and support
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[70] transition-opacity duration-300"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-xl z-[80] transform transition-transform duration-300 ease-in-out overflow-y-auto ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Menu</h2>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="hover:text-[#6B9FA8] transition-colors"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Drawer Content */}
        <nav className="p-6">
          <div className="space-y-4">
            <Link
              to="/"
              onClick={() => setIsDrawerOpen(false)}
              className="block text-base font-medium text-gray-900 hover:text-[#6B9FA8] transition-colors py-2"
            >
              Home
            </Link>

            {/* Categories */}
            {isLoadingCategories ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-6 bg-gray-200 rounded animate-pulse"></div>
                ))}
              </div>
            ) : (
              categories.map((category) => (
                <Link
                  key={category._id || category.name}
                  to={`/category/${category._id}`}
                  onClick={() => setIsDrawerOpen(false)}
                  className="block text-base font-medium text-gray-900 hover:text-[#6B9FA8] transition-colors py-2"
                >
                  {category.name || category.category}
                </Link>
              ))
            )}

            {/* Pages Section */}
            <div>
              <button
                onClick={() => setIsPagesExpanded(!isPagesExpanded)}
                className="flex items-center justify-between w-full text-base font-medium text-gray-900 hover:text-[#6B9FA8] transition-colors py-2"
              >
                <span>Pages</span>
                <ChevronDown
                  className={`w-5 h-5 transition-transform duration-200 ${isPagesExpanded ? 'rotate-180' : ''
                    }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${isPagesExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="pl-4 pt-2 space-y-2">
                  <Link
                    to="/aboutus"
                    onClick={() => {
                      setIsDrawerOpen(false);
                      setIsPagesExpanded(false);
                    }}
                    className="block text-sm text-gray-700 hover:text-[#6B9FA8] transition-colors py-2"
                  >
                    About us
                  </Link>
                  <Link
                    to="/contactus"
                    onClick={() => {
                      setIsDrawerOpen(false);
                      setIsPagesExpanded(false);
                    }}
                    className="block text-sm text-gray-700 hover:text-[#6B9FA8] transition-colors py-2"
                  >
                    Contact us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Navbar;
